import { FC, Fragment, ReactNode, useState } from 'react';

import { XIcon } from '@heroicons/react/outline';
import { GiftOrderForAdmin } from '@shared/api/giftOrder/giftOrder.interface';

import { CouponStats } from './types';

/**
 * 헤더 액션 버튼 — 조건 미충족이어도 버튼은 항상 보이고, 비활성 사유를
 * 클릭 팝오버로 안내한다 (조건부로 버튼이 사라지면 기능 존재 자체를 모르게 됨).
 * group이 있으면 같은 라벨끼리 라벨 달린 박스로 모아 배치한다(접지 않고 전부 열거).
 * role 게이트(권한상 아예 못 쓰는 기능)는 호출부에서 배열에 넣지 않는 것으로 처리.
 */
export interface CouponHeaderAction {
  key: string;
  label: string;
  /** 대상 건수 배지 — undefined면 미표시 */
  count?: number;
  /** 활성 상태 hover 설명 */
  hint: string;
  /** 비활성 사유 — 값이 있으면 비활성 표시, 클릭 시 사유 팝오버 */
  disabledReason?: string | null;
  /** 비활성 팝오버 안의 다음 단계 바로가기 (예: "기입폼 업로드 열기") */
  disabledCta?: { label: string; onClick: () => void };
  onClick: () => void;
  /** 활성 상태 색상 클래스 */
  enabledClassName: string;
  /** 그룹 라벨 — 연속된 같은 라벨의 액션끼리 하나의 그룹 박스로 묶어 표시 */
  group?: string;
}

// 연속된 같은 group 라벨의 액션을 하나의 그룹 박스로 묶는다
const groupActions = (
  actions: CouponHeaderAction[],
): Array<{ label?: string; items: CouponHeaderAction[] }> => {
  const groups: Array<{ label?: string; items: CouponHeaderAction[] }> = [];
  for (const a of actions) {
    const last = groups[groups.length - 1];
    if (last && last.label === a.group) last.items.push(a);
    else groups.push({ label: a.group, items: [a] });
  }
  return groups;
};

interface CouponDetailHeaderProps {
  stats: CouponStats;
  orderId?: string;
  order?: GiftOrderForAdmin;
  previewCopied: boolean;
  setPreviewCopied: (v: boolean) => void;
  onClose: () => void;
  /** 헤더 액션 버튼들 (비슷한 기능끼리 인접 배치된 순서) */
  actions: CouponHeaderAction[];
  /** 액션 버튼 오른쪽에 붙일 자체 렌더 요소 (CS 메모장 버튼 등) */
  extraActions?: ReactNode;
}

export const CouponDetailHeader: FC<CouponDetailHeaderProps> = ({
  stats,
  orderId,
  order,
  previewCopied,
  setPreviewCopied,
  onClose,
  actions,
  extraActions,
}) => {
  // 비활성 버튼 클릭 시 사유 팝오버 — 한 번에 하나만
  const [reasonKey, setReasonKey] = useState<string | null>(null);

  const renderAction = (a: CouponHeaderAction) => {
    const disabled = !!a.disabledReason;
    return (
      <span key={a.key} className="relative">
        <button
          onClick={() => {
            if (disabled) setReasonKey((cur) => (cur === a.key ? null : a.key));
            else a.onClick();
          }}
          title={
            disabled
              ? '지금은 사용할 수 없어요 — 클릭하면 이유를 알려드려요'
              : a.hint
          }
          className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${
            disabled
              ? 'cursor-help border-gray-200 bg-gray-50 text-gray-400'
              : `bg-white ${a.enabledClassName}`
          }`}
        >
          {a.label}
          {a.count !== undefined && (
            <span
              className={`ml-1.5 rounded-full px-1.5 py-px text-[10px] font-bold tabular-nums ${
                disabled ? 'bg-gray-200 text-gray-400' : 'bg-black/5'
              }`}
            >
              {a.count}
            </span>
          )}
        </button>
        {reasonKey === a.key && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setReasonKey(null)} />
            <div className="absolute right-0 top-full z-20 mt-1.5 w-72 rounded-lg border border-gray-200 bg-white p-3 text-left shadow-xl">
              <p className="text-xs font-bold text-gray-800">
                {a.label} — 지금은 사용할 수 없어요
              </p>
              <p className="mt-1 whitespace-pre-line text-xs leading-relaxed text-gray-500">
                {a.disabledReason}
              </p>
              {a.disabledCta && (
                <button
                  onClick={() => {
                    setReasonKey(null);
                    a.disabledCta?.onClick();
                  }}
                  className="mt-2 w-full rounded-md bg-gray-800 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-gray-700"
                >
                  {a.disabledCta.label} →
                </button>
              )}
            </div>
          </>
        )}
      </span>
    );
  };

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-y-2 px-5 py-3">
      <div className="flex items-center gap-3">
        <h2 className="text-base font-bold text-gray-900">쿠폰 상세내역</h2>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>
            전체 <span className="font-semibold text-gray-700">{stats.total}</span>
            {stats.additionalTotal > 0 && (
              <span className="ml-0.5 font-semibold text-amber-600">
                (+{stats.additionalTotal}재발행)
              </span>
            )}
            {stats.reissuedCount > 0 && (
              <span className="ml-0.5 font-semibold text-teal-600">
                (+{stats.reissuedCount}재발급)
              </span>
            )}
          </span>
          {stats.disposedCount > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span>
                폐기{' '}
                <span className="font-semibold text-slate-500">
                  {stats.disposedCount}
                </span>
              </span>
            </>
          )}
          <span className="text-gray-300">|</span>
          <span>
            미공유{' '}
            <span
              className={`font-semibold ${
                stats.notShared > 0 ? 'text-orange-600' : 'text-gray-400'
              }`}
            >
              {stats.notShared}
            </span>
          </span>
          <span className="text-gray-300">|</span>
          <span>
            미사용{' '}
            <span
              className={`font-semibold ${
                stats.available > 0 ? 'text-blue-600' : 'text-gray-400'
              }`}
            >
              {stats.available}
            </span>
          </span>
          <span className="text-gray-300">|</span>
          <span>
            교환{' '}
            <span
              className={`font-semibold ${
                stats.delivered > 0 ? 'text-green-600' : 'text-gray-400'
              }`}
            >
              {stats.delivered}
            </span>
          </span>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {groupActions(actions).map((g, gi) =>
          g.label ? (
            <div
              key={`group-${g.label}`}
              className="flex items-center gap-1.5 rounded-xl bg-gray-50 py-1 pl-2 pr-1 ring-1 ring-gray-100"
            >
              <span className="text-[10px] font-semibold tracking-wide text-gray-400">
                {g.label}
              </span>
              {g.items.map(renderAction)}
            </div>
          ) : (
            <Fragment key={`group-${gi}`}>{g.items.map(renderAction)}</Fragment>
          ),
        )}
        {extraActions}
        {orderId && order && (
          <button
            onClick={() => {
              const name = encodeURIComponent(order.receiverName ?? '');
              const phone = encodeURIComponent(order.receiverPhoneNumber ?? '');
              const key = encodeURIComponent(
                process.env.REACT_APP_GIFT_STORE_ADMIN_PREVIEW_KEY ?? '',
              );
              const bugoId = order.bugo?._id;
              const bugoQuery = bugoId
                ? `&bugoId=${encodeURIComponent(bugoId)}`
                : '';
              const url = `${window.location.origin}/gift-store/order/${orderId}/coupon?name=${name}&phoneNumber=${phone}&secretKey=${key}${bugoQuery}`;
              navigator.clipboard.writeText(url).then(() => {
                setPreviewCopied(true);
                setTimeout(() => setPreviewCopied(false), 2000);
              });
            }}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
              previewCopied
                ? 'bg-green-50 text-green-600'
                : 'text-indigo-600 hover:bg-indigo-50'
            }`}
          >
            {previewCopied ? '복사됨!' : '미리보기 URL 복사'}
          </button>
        )}
        <button
          onClick={onClose}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <XIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};
